/**
 * 一个帮助做二次验证的流程框架
 */

import { getResultFromResponse, Response } from './request';
import { openCaptchaV2 } from './uniframe';
import { runAsyncSafe } from './utils';

export type ProcedureOption<Param extends Record<string, unknown>, T> = {
  // 校验函数
  verifyFunc: (
    arg?: Record<string, unknown>
  ) => Promise<Record<string, unknown>>;
  // action的结果是否需要调用 二次验证，这个函数可以返回一个给
  shouldVerify: (
    params: Param,
    isFirst: boolean,
    lastRes?: [err?: any, res?: T | null]
  ) => boolean | Record<string, unknown>;

  // 最大verify 次数，默认为 1，即只要让用户验证过一次，就不再拦截action的请求了，就不主动再让用户校验
  maxVerify?: number;
};

export const buildVerifyProcedure = <Param extends Record<string, unknown>, T>(
  action: (arg: Param) => Promise<T>,
  { shouldVerify, verifyFunc, maxVerify = 1 }: ProcedureOption<Param, T>
) => {
  return (param: Param) => {
    let verifyTime = 0;
    let runRes: Awaited<ReturnType<typeof runAsyncSafe<T>>> | undefined;

    const invokeAction = async () => {
      // 第一次运行，或未达到验证次数
      if (!runRes || verifyTime < maxVerify) {
        // 获取是否需要进行认证（有返回一个认证参数对象就代表需要认证）
        const verifyArg = shouldVerify(param, !runRes, runRes);
        if (verifyArg) {
          verifyTime += 1;
          const [vErr, vRes] = await runAsyncSafe(() =>
            verifyFunc(verifyArg === true ? {} : verifyArg)
          );
          if (vRes) {
            // 验证成功，将验证的参数（一般是后端需要的token）传入action，并记录执行结果，并进入下一次
            runRes = await runAsyncSafe(() =>
              action({
                ...param,
                ...vRes,
              })
            );
            return invokeAction();
          }
          //   验证码校验有问题，就直接抛出验证错误
          throw vErr;
        }
        if (!runRes) {
          // 不需要校验的情况下，如果没有执行过，就继续支持，不然就往下返回数据了
          runRes = await runAsyncSafe(() => action(param));
          return invokeAction();
        }
      }
      if (runRes[0]) {
        throw runRes[0];
      }
      return runRes[1] as T;
    };

    return invokeAction();
  };
};

// 创建接口二次校验过程
export const buildApiRequestCaptcha = <
  Param extends Record<string, unknown>,
  T,
>(
  action: (arg: Param) => Promise<Response<T>>,
  opt: Omit<
    ProcedureOption<Param, Response<T>>,
    'verifyFunc' | 'shouldVerify'
  > & {
    // 传给接口的captcha token参数
    captchaParamKey: string;
    // 接口报captcha接口错误的 错误码是多少
    captchaErrorCode: number;
    // 首次请求是否先进行验证
    verifyFirstTime: boolean;
    // 场景id
    senceId: string;
  }
) => {
  return buildVerifyProcedure(
    (param: Param) => getResultFromResponse(action(param)),
    {
      maxVerify: opt.maxVerify ?? 1,
      shouldVerify: (_p, isFirst, [err] = []) => {
        if (isFirst) {
          return opt.verifyFirstTime;
        }
        if (err?.code === opt.captchaErrorCode) {
          return true;
        }
        return false;
      },
      verifyFunc: async () => {
        const res = await openCaptchaV2({
          scene: opt.senceId,
        });
        return {
          [opt.captchaParamKey]: res.serialNo,
        };
      },
    }
  );
};
